/*
 * <live-counters> — product page viewers + low stock.
 * Real mode: real low stock of the selected variant only (no viewers).
 * Demo mode: random drifting viewers + session-stable random stock.
 */
(() => {
  if (customElements.get('live-counters')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TOKEN = '__n__';

  const toNum = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const readRange = (src, fallback) => {
    let min = Math.round(toNum(src && src.min, fallback[0]));
    let max = Math.round(toNum(src && src.max, fallback[1]));
    if (min > max) [min, max] = [max, min];
    return [Math.max(0, min), Math.max(0, max)];
  };
  const readJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };
  const storage = {
    get(key) {
      try {
        return JSON.parse(window.sessionStorage.getItem(key));
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable: keep in memory only */
      }
    },
  };

  class LiveCounters extends HTMLElement {
    connectedCallback() {
      this.timers = [];
      this.cfg = readJSON(this.querySelector('[data-live-counters-config]')) || {};
      this.mode = this.dataset.mode === 'demo' ? 'demo' : 'real';
      this.handle = this.dataset.handle || '';
      this.threshold = toNum(this.dataset.threshold, 10);
      this.barMax = Math.max(1, toNum(this.dataset.barMax, 20));
      this.stockOn = this.dataset.stockOn !== 'false';

      this.viewersRow = this.querySelector('[data-lc-viewers]');
      this.viewersText = this.querySelector('[data-lc-viewers-text]');
      this.stockRow = this.querySelector('[data-lc-stock]');
      this.stockText = this.querySelector('[data-lc-stock-text]');
      this.chip = this.querySelector('[data-lc-chip]');
      this.fill = this.querySelector('[data-lc-fill]');
      if (this.chip) this.chip.textContent = this.copy('lowBar', 'tLowBar');

      this.info = this.closest('product-info');
      const productData = this.info ? readJSON(this.info.querySelector('[data-product-json]')) : null;
      const variants = (productData && productData.variants) || [];
      this.variant = variants.find((v) => String(v.id) === this.dataset.variantId) || null;

      this.onVariantChange = (event) => {
        this.variant = (event.detail && event.detail.variant) || null;
        this.render();
      };
      if (this.info) this.info.addEventListener('product:variant-change', this.onVariantChange);

      this.onVisibility = () => {
        if (!document.hidden) this.scheduleDrift();
      };

      if (this.cfg.enabled === false || (this.mode === 'demo' && !this.demoEligible())) {
        this.hidden = true;
        this.off = true;
        return;
      }
      if (this.mode === 'demo') this.initDemo();
      this.render();
    }

    disconnectedCallback() {
      this.timers.forEach((t) => clearTimeout(t));
      this.timers = [];
      if (this.info) this.info.removeEventListener('product:variant-change', this.onVariantChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* Copy: metafield {n} templates on the primary locale, else theme locale (__n__). */
    copy(key, dataKey) {
      const custom = this.cfg.copy && this.cfg.copy[key];
      if (this.dataset.customCopy === 'true' && typeof custom === 'string' && custom.trim()) {
        return custom.replace(/\{\s*n\s*\}/g, TOKEN);
      }
      return this.dataset[dataKey] || '';
    }

    /* Writes "before <num> after"; returns the number element for later ticks. */
    writeTemplate(el, template, n) {
      el.textContent = '';
      const parts = template.split(TOKEN);
      const num = document.createElement('span');
      num.className = 'live-counters__num';
      num.textContent = String(n);
      parts.forEach((part, i) => {
        if (i > 0) el.append(i === 1 ? num : String(n));
        if (part) el.append(part);
      });
      if (parts.length === 1) el.append(' ', num);
      return num;
    }

    tick(num, n) {
      if (!num || num.textContent === String(n)) return;
      num.textContent = String(n);
      if (reduceMotion.matches) return;
      num.classList.remove('is-ticking');
      void num.offsetWidth;
      num.classList.add('is-ticking');
    }

    /* ---------- Demo data ---------- */
    override() {
      const o = this.cfg.overrides && this.cfg.overrides[this.handle];
      return o && typeof o === 'object' ? o : null;
    }

    /* applyTo "selected" (default): only listed handles; "all": every product. */
    demoEligible() {
      const o = this.override();
      if (o && o.enabled === false) return false;
      return this.cfg.applyTo === 'all' || !!o;
    }

    initDemo() {
      const override = this.override() || {};
      const viewersCfg = this.cfg.viewers || {};
      const stockCfg = this.cfg.stock || {};
      const ovStock = override.stock || {};
      const fixed = Number(ovStock.fixed);

      if (this.viewersRow) {
        this.viewersRange = readRange(override.viewers, readRange(viewersCfg, [12, 48]));
        const steps = Array.isArray(viewersCfg.stepSeconds) ? viewersCfg.stepSeconds : [7, 18];
        const a = Math.max(2, toNum(steps[0], 7));
        const b = Math.max(a, toNum(steps[1], 18));
        this.stepRange = [a, b];
        this.viewers = randInt(this.viewersRange[0], this.viewersRange[1]);
      }

      if (this.stockOn && Number.isInteger(fixed) && fixed > 0) {
        /* Fixed stock: exact, stable, no drift. */
        this.demoStock = fixed;
      } else if (this.stockOn) {
        const [min, max] = readRange(ovStock, readRange(stockCfg, [3, 14]));
        const low = Math.max(1, min);
        const high = Math.max(low, Math.min(max, this.threshold));
        const key = `burnoutgarage:live-stock:${this.handle}`;
        let saved = storage.get(key);
        if (!saved || typeof saved.n !== 'number' || saved.n < low || saved.n > high) {
          saved = { n: randInt(low, high), dropped: false };
          storage.set(key, saved);
        }
        this.demoStock = saved.n;

        /* At most one slow decrease per session visit, after the shopper has lingered. */
        this.timers.push(
          setTimeout(() => {
            if (!saved.dropped && this.demoStock > low && Math.random() < 0.5) {
              this.demoStock -= 1;
              storage.set(key, { n: this.demoStock, dropped: true });
              this.render();
            }
          }, randInt(45, 90) * 1000)
        );
      }

      if (this.viewersRow && !reduceMotion.matches) {
        document.addEventListener('visibilitychange', this.onVisibility);
        this.scheduleDrift();
      }
    }

    scheduleDrift() {
      clearTimeout(this.driftTimer);
      if (reduceMotion.matches || document.hidden || !this.viewersRange) return;
      const delay = randInt(this.stepRange[0] * 1000, this.stepRange[1] * 1000);
      this.driftTimer = setTimeout(() => {
        if (document.hidden) return;
        const [min, max] = this.viewersRange;
        const step = randInt(1, 3) * (Math.random() < 0.5 ? -1 : 1);
        let next = this.viewers + step;
        if (next < min || next > max) next = this.viewers - step;
        this.viewers = clamp(next, min, max); /* never leaves the range */
        this.tick(this.viewersNum, this.viewers);
        this.scheduleDrift();
      }, delay);
      this.timers.push(this.driftTimer);
    }

    /* ---------- Render ---------- */
    stockCount() {
      const v = this.variant;
      if (!v || v.available === false || !this.stockOn) return null;
      if (this.mode === 'demo') return this.demoStock || null;
      if (v.inventoryManagement !== 'shopify') return null;
      const n = toNum(v.inventoryQuantity, NaN);
      if (!Number.isFinite(n) || n <= 0 || n > this.threshold) return null;
      return n;
    }

    render() {
      if (this.off) return;
      const soldOut = !this.variant || this.variant.available === false;

      let showViewers = false;
      if (this.viewersRow) {
        showViewers = this.mode === 'demo' && !soldOut && typeof this.viewers === 'number';
        if (showViewers && !this.viewersNum) {
          this.viewersNum = this.writeTemplate(this.viewersText, this.copy('viewers', 'tViewers'), this.viewers);
          /* Announce once, then stop announcing the drifting number. */
          this.timers.push(setTimeout(() => this.viewersText.setAttribute('aria-live', 'off'), 1500));
        }
        this.viewersRow.hidden = !showViewers;
        this.viewersRow.classList.remove('is-pending');
      }

      const n = this.stockCount();
      if (this.stockRow) {
        if (n !== null) {
          const template = n === 1 ? this.copy('stock', 'tStockOne') : this.copy('stockPlural', 'tStockOther');
          if (this.stockText.dataset.n !== String(n) || this.stockText.dataset.tpl !== template) {
            this.writeTemplate(this.stockText, template, n);
            this.stockText.dataset.n = String(n);
            this.stockText.dataset.tpl = template;
          }
          const pct = clamp(Math.round((n / this.barMax) * 100), 8, 100);
          if (this.fill) this.fill.style.setProperty('--lc-fill', `${pct}%`);
        } else {
          delete this.stockText.dataset.n;
        }
        this.stockRow.hidden = n === null;
        this.stockRow.classList.remove('is-pending');
      }

      this.hidden = !showViewers && n === null;
      this.classList.add('is-ready');
    }
  }

  customElements.define('live-counters', LiveCounters);
})();
